/**
 * Swarm steering.
 *
 * The bar is Geometry Wars: Retro Evolved 2. What makes those swarms read as
 * alive is not the boid rules on their own — it is four things layered:
 *
 *  1. Separation dominates, and it has a *tangential* component, so agents
 *     slide around each other instead of shoving head-on and vibrating.
 *  2. The path force targets a point ahead on the spline rather than the
 *     nearest point, so a turn is anticipated and the mass swings wide.
 *  3. Per-agent wander from seeded value noise. Two agents with identical
 *     neighbours still do not move identically, and a run still reproduces.
 *  4. Heading is a spring, not an assignment (see `Enemy.updateHeading`).
 *
 * Cost control: one spatial hash rebuild per step, shared by every rule. In a
 * 400-body pile-up each agent would see hundreds of candidates, so each agent
 * samples a fixed budget of them with a per-agent stride derived from last
 * step's candidate count. The stride offset is derived from the agent index and
 * held fixed: rotating it with `world.tick` turned the neighbour set over
 * completely every four frames, which resampled the separation force from
 * scratch at 15Hz and showed up as 4.8 degrees per frame of direction churn.
 * The subset still refreshes as bodies move between hash cells, just not at
 * 60Hz.
 */

import { FLOCK, VIEW } from '@/core/config';
import { SpatialHash } from '@/core/spatial';
import type { Enemy, System, World } from '@/core/types';
import {
  getActiveTrack, makeTrackSample, type Track, type TrackSample,
} from '@/game/Track';
import { noise1, type EnemyInternal } from '@/game/entities/Enemy';

const TAU = Math.PI * 2;

/**
 * Neighbours actually folded into the rules per agent, per step. Measured
 * candidate count in a 460-body swarm is around 90, so 22 was a quarter sample
 * and the separation force was reconstructed from a different quarter each
 * time. Cost is linear and the hash query already visited all of them.
 */
const SAMPLE_BUDGET = 40;
/** Tangential share of the separation force. Zero here is jitter-lock. */
const SWIRL = 0.58;
/**
 * Summed separation magnitude that counts as "normal crowding".
 *
 * One neighbour touching contributes about 1.16 to `sm`, so a handful of
 * partly-cancelling neighbours lands near this. Dividing by it turns the raw
 * sum into a multiplier centred on 1: the rule keeps its 150:1 urgency range
 * without inflating the weight the whole blend is scaled against.
 */
const SEP_REF = 0.9;
/** Ceiling on that multiplier. Above this the agent is simply interpenetrating. */
const SEP_URGENCY_MAX = 3;
/**
 * How far the per-agent path target sits off the centreline, as a multiple of
 * corridor half-width.
 *
 * This is the cheap way to buy spacing. Cranking separation instead costs
 * speed and direction stability steeply — measured, going from urgency 3 to 5
 * bought 1px of nearest-neighbour distance and gave up 26 points of nominal
 * speed and 8 degrees per frame of churn. Spreading the *attractor* buys the
 * same spacing for free, because agents are no longer converging on one line
 * and then shoving each other off it.
 */
const LANE_SPREAD = 1.6;
/** Pull back toward the corridor once past the half-width. */
const CONTAIN_WEIGHT = 2.1;
/**
 * Soft wall, measured *outside* the buffer. Map entry tails start as far as
 * 60px off-screen, so a wall on the buffer edge would crush an entering
 * cluster against it. This only catches agents thrown genuinely clear.
 */
const EDGE = -84;
/**
 * How hard an agent chases its desired velocity, in reciprocal seconds. Low
 * enough that a heading change takes about a fifth of a second to land, which
 * is the inertia the swarm needs to read as having mass.
 */
const RESPONSE = 7;

const NR = FLOCK.neighbourRadius;
const NR2 = NR * NR;
const SEP_R = FLOCK.separationRadius;

const hash = new SpatialHash(VIEW.width, VIEW.height, NR, 8192);

/** Compacted list of flocking agents for this step. Reused, never reallocated. */
const agents: Enemy[] = [];

const getX = (i: number): number => agents[i].pos.x;
const getY = (i: number): number => agents[i].pos.y;

// ── per-agent scratch, module scoped so the visitor closure is hoisted ──

let curIdx = 0;
let curX = 0, curY = 0, curVX = 0, curVY = 0;
let sepX = 0, sepY = 0, aliX = 0, aliY = 0, cohX = 0, cohY = 0;
let nNear = 0, nSep = 0;
let candidates = 0, strideMod = 1, strideOff = 0;

const visit = (j: number): void => {
  candidates++;
  if (strideMod > 1 && (candidates + strideOff) % strideMod !== 0) return;
  if (j === curIdx) return;

  const o = agents[j];
  const dx = o.pos.x - curX;
  const dy = o.pos.y - curY;
  const d2 = dx * dx + dy * dy;
  if (d2 >= NR2 || d2 < 1e-6) return;

  nNear++;
  aliX += o.vel.x; aliY += o.vel.y;
  cohX += o.pos.x; cohY += o.pos.y;

  const d = Math.sqrt(d2);
  if (d < SEP_R) {
    // Falloff over the separation radius, divided by distance so the nearest
    // body dominates. Both factors fold into one reciprocal.
    const w = (SEP_R - d) / (SEP_R * d);
    sepX -= dx * w;
    sepY -= dy * w;
    // Slide around, not through. Which way depends on which side of my own
    // heading the neighbour sits, so a pair consistently passes rather than
    // trading pushes at 60Hz.
    const side = curVX * dy - curVY * dx >= 0 ? 1 : -1;
    sepX += -dy * w * SWIRL * side;
    sepY += dx * w * SWIRL * side;
    nSep++;
  }
};

// ── steering accumulator ──

let dirX = 0, dirY = 0;

/**
 * Blends one rule into the desired heading.
 *
 * Note what this is *not*: classic Reynolds computes `desired - velocity` per
 * rule and sums those. Do that with six rules and the summed `-velocity` terms
 * act as a drag of `Σweights × v`, so a crowded agent settles at a fraction of
 * its speed and the whole swarm crawls. Blend unit directions first, convert
 * to a desired velocity once, and steer once — then a jostling agent still
 * travels at full pace, it just travels somewhere slightly different.
 */
function push(dx: number, dy: number, weight: number): void {
  const m = Math.hypot(dx, dy);
  if (m < 1e-5) return;
  const k = weight / m;
  dirX += dx * k;
  dirY += dy * k;
}

const here: TrackSample = makeTrackSample();
const ahead: TrackSample = makeTrackSample();
const flowVec = { x: 0, y: 0 };

// ─────────────────────────────────────────────────────────────────── step ──

/**
 * Fills `acc` for every flocking enemy. Must run before `stepEnemies`, which
 * integrates and clears it.
 */
export function stepFlock(dt: number, world: World, trackArg?: Track): void {
  const track = trackArg ?? getActiveTrack();
  if (!track) return;

  agents.length = 0;
  const list = world.enemies;
  for (let i = 0; i < list.length; i++) {
    const e = list[i];
    if (e.alive && e.archetype.flocks) agents.push(e);
  }
  const n = agents.length;
  if (n === 0) return;

  hash.rebuild(n, getX, getY);

  const now = world.time;

  for (let i = 0; i < n; i++) {
    const e = agents[i] as EnemyInternal;
    const a = e.archetype;

    curIdx = i;
    curX = e.pos.x; curY = e.pos.y;
    curVX = e.vel.x; curVY = e.vel.y;
    sepX = 0; sepY = 0; aliX = 0; aliY = 0; cohX = 0; cohY = 0;
    nNear = 0; nSep = 0; candidates = 0;

    strideMod = e.neighbourEst > SAMPLE_BUDGET
      ? Math.ceil(e.neighbourEst / SAMPLE_BUDGET)
      : 1;
    strideOff = strideMod > 1 ? i % strideMod : 0;

    hash.query(curX, curY, NR, visit);
    e.neighbourEst = candidates;

    const maxSpeed = a.speed * e.speedVar;
    dirX = 0; dirY = 0;

    // ── separation ──
    // Deliberately not routed through `push`. Every other rule carries
    // direction only, but `sepX/sepY` accumulates `(SEP_R - d) / (SEP_R * d)`,
    // which spans 150:1 between a body 1px away and one 12px away — that range
    // *is* the urgency signal. Normalising it away made a near-touching pair
    // push exactly as hard as a comfortable one, and in an isotropic pack the
    // summed vector nearly cancels, so normalising the residual amplified pure
    // noise back to full weight.
    if (nSep > 0) {
      const sm = Math.hypot(sepX, sepY);
      if (sm > 1e-5) {
        // No neighbour-count multiplier any more. That term existed to fake a
        // density signal back in after normalisation threw it away; keeping it
        // on top of the restored magnitude counts density twice, and measured
        // 16:1 against `pathWeight` in the core — the swarm stalled at 33% of
        // nominal speed and spun at 26 degrees of direction churn per frame.
        const urgency = Math.min(SEP_URGENCY_MAX, sm / SEP_REF);
        const k = (FLOCK.separationWeight * urgency) / sm;
        dirX += sepX * k;
        dirY += sepY * k;
      }
    }

    // ── alignment and cohesion ──
    if (nNear > 0) {
      const inv = 1 / nNear;
      push(aliX * inv, aliY * inv, FLOCK.alignmentWeight);
      push(cohX * inv - curX, cohY * inv - curY, FLOCK.cohesionWeight);
    }

    // ── path following ──
    // Aim at a point ahead on the spline. Anticipating the corner is what
    // makes the mass swing wide through a turn instead of hugging the inside
    // and folding in on itself.
    //
    // Each agent aims at *its own* point, not the shared centreline. `acquire`
    // already hands every flocker a persistent lane in [-0.9, 0.9] and until
    // now only `followSpline` read it — it was dead state on the whole flocking
    // family. Aiming 460 bodies at one 1-D attractor is what made the outer
    // boundary of the mass a smooth offset curve of the corridor, the single
    // most artificial thing in the swarm stills.
    const look = 16 + maxSpeed * 0.36;
    let target = e.progress + look;
    if (target > track.length) target = track.length;
    else if (target < 0) target *= 0.5;
    track.sampleExtended(target, ahead);
    const laneOff = e.lane * ahead.w * LANE_SPREAD;
    push(
      ahead.x - ahead.ty * laneOff - curX,
      ahead.y + ahead.tx * laneOff - curY,
      FLOCK.pathWeight,
    );

    // ── corridor containment ──
    // Deliberately loose. Four hundred bodies cannot physically fit a 40px
    // corridor: at 43px² each the perfectly-even spacing would be 6.6px,
    // already under the 13px separation radius, so the target is geometrically
    // unreachable and separation saturates into a fluid at maximum packing.
    // A 1.8x band kept the mass a corridor-shaped tube; 3.2x put the gate at
    // 62px against a measured P95 drift of 48px, so the rule was dead code for
    // 95% of the swarm and half the mass sat outside a 39px-wide path. 1.4x
    // sets the foot at 27px, under the resting drift, so with the
    // `min(2.2, over)` ramp below it engages continuously and proportionally —
    // a spring, not a fence. Measured after: offMean 13.6-14.2, offP95 30.1,
    // churn 2.8°/frame, 84% of nominal speed.
    track.sampleExtended(e.progress, here);
    const driftX = curX - here.x;
    const driftY = curY - here.y;
    const drift = Math.hypot(driftX, driftY);
    const room = Math.max(4, here.w) * 1.4;
    const over = (drift - room) / room;

    if (over > 0) {
      push(-driftX, -driftY, CONTAIN_WEIGHT * Math.min(2.2, over));

      // Genuinely lost — a shockwave threw it clear of the corridor. One array
      // read from the flow field tells it which way downstream is without a
      // projection search.
      if (over > 0.9) {
        track.flowAt(curX, curY, flowVec);
        push(flowVec.x, flowVec.y, FLOCK.pathWeight * 0.9 * Math.min(1, over - 0.9));
      }
      e.flowBlend = Math.min(1, over);
    } else {
      e.flowBlend = 0;
    }

    // ── wander ──
    // Seeded value noise, not Math.random: a replayed run must produce the
    // same swarm, and a sine hash bands badly at this frequency.
    //
    // The noise swings the angle around `e.phase`, the agent's own fixed axis,
    // rather than mapping onto the whole circle. Mapping [0,1) onto TAU*2 let
    // the vector sweep two full turns between noise nodes, roughly 11 degrees
    // per frame, and a vector spinning that fast integrates to nothing over the
    // 143ms velocity time constant. The rule contributed dither, not intent.
    const ang = e.phase + (noise1(e.seed, now * FLOCK.wanderHz) * 2 - 1) * 1.2;
    push(Math.cos(ang), Math.sin(ang), FLOCK.wanderWeight);

    // ── soft walls ──
    if (curX < EDGE) push(1, 0, (EDGE - curX) * 0.5);
    else if (curX > VIEW.width - EDGE) push(-1, 0, (curX - (VIEW.width - EDGE)) * 0.5);
    if (curY < EDGE) push(0, 1, (EDGE - curY) * 0.5);
    else if (curY > VIEW.height - EDGE) push(0, -1, (curY - (VIEW.height - EDGE)) * 0.5);

    // ── one steering step toward the blended heading ──
    const m = Math.hypot(dirX, dirY);
    let accX: number, accY: number;
    if (m < 1e-5) {
      accX = -curVX * RESPONSE;
      accY = -curVY * RESPONSE;
    } else {
      accX = ((dirX / m) * maxSpeed - curVX) * RESPONSE;
      accY = ((dirY / m) * maxSpeed - curVY) * RESPONSE;
    }

    const f = Math.hypot(accX, accY);
    if (f > FLOCK.maxForce) {
      const k = FLOCK.maxForce / f;
      accX *= k;
      accY *= k;
    }

    e.acc.x = accX;
    e.acc.y = accY;
  }

  void dt;
}

/** Number of agents the last step steered. For the debug overlay. */
export const flockSize = (): number => agents.length;

export function createFlockSystem(track?: Track): System {
  return {
    name: 'flock',
    step(dt: number, world: World): void {
      stepFlock(dt, world, track);
    },
  };
}
